/* eslint-disable */
import React, { useMemo, useState } from "react";
import {
  Stack,
  Title,
  Card,
  Grid,
  Text,
  Badge,
  Group,
  Button,
  Box,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconClock,
  IconUsers,
  IconVideo,
  IconFolder,
  IconPlayerPlay,
  IconEye,
} from "@tabler/icons-react";
import { useGetTodayMeetings } from "api/meeting/getTodayMeeting";
import MeetingDetailsModal from "../meetingDetail/meetingDetailModal";

type Attendee = {
  user: string;
  userType: string;
  status?: "pending" | "accepted" | "declined";
};

export type Meeting = {
  id: string;
  title: string;
  description?: string;
  scheduledDate: string;
  duration: number;
  status: "scheduled" | "ongoing" | "completed" | "cancelled";
  meetingPlatform?: string;
  meetingLink?: string;
  attendees: Attendee[];
  agenda?: string;
  projectId?: string;
  isRecurring?: boolean;
};

interface Props {
  onJoin?: (meeting: Meeting) => void;
  onView?: (meetingId: string) => void;
}

export default function TodayMeetings({ onJoin, onView }: Props) {
  const { data } = useGetTodayMeetings();
  const [openedMeetingModal, setOpenedMeetingModal] = useState(false);
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);
  type ApiAttendeeUserObj = {
    _id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  type ApiAttendee = {
    user?: string | ApiAttendeeUserObj;
    userType?: string;
    status?: "pending" | "accepted" | "declined";
    joinedAt?: string;
    _id?: string;
  };
  type ApiMeeting = {
    _id?: string;
    id?: string;
    title?: string;
    description?: string;
    attendees?: ApiAttendee[];
    scheduledDate?: string;
    duration?: number | string;
    status?: "scheduled" | "ongoing" | "completed" | "cancelled";
    meetingLink?: string;
    meetingPlatform?: string;
    projectId?: string | { _id?: string; name?: string };
    agenda?: string;
    isRecurring?: boolean;
  };

  const apiMeetings: Meeting[] = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];

    return (data.data as any[]).map((am: ApiMeeting) => {
      const id = String(am._id ?? am.id ?? "");
      const scheduledDate = am.scheduledDate ?? new Date().toISOString();
      const rawDuration = am.duration ?? 0;
      const duration =
        typeof rawDuration === "number"
          ? rawDuration
          : Number(rawDuration) || 0;

      const attendees: Attendee[] = Array.isArray(am.attendees)
        ? am.attendees.map((a) => {
            let userId = "";
            if (!a) {
              userId = "";
            } else if (typeof a.user === "string") {
              userId = a.user;
            } else if (a.user && typeof a.user === "object") {
              userId = String(a.user._id ?? a.user.email ?? "");
            } else {
              userId = "";
            }

            return {
              user: userId,
              userType: String(a?.userType ?? "User"),
              status: a?.status as Attendee["status"],
            };
          })
        : [];

      let projectDisplay = "";
      if (am.projectId) {
        if (typeof am.projectId === "string") projectDisplay = am.projectId;
        else if (typeof am.projectId === "object")
          projectDisplay =
            (am.projectId as any).name ?? (am.projectId as any)._id ?? "";
      }

      const mapped: Meeting = {
        id,
        title: am.title ?? "Untitled meeting",
        description: am.description,
        scheduledDate,
        duration,
        status: (am.status as Meeting["status"]) ?? "scheduled",
        meetingPlatform: am.meetingPlatform,
        meetingLink: am.meetingLink,
        attendees,
        agenda: am.agenda,
        projectId: projectDisplay || undefined,
        isRecurring: !!am.isRecurring,
      };

      return mapped;
    });
  }, [data]);

  const combinedMeetings = apiMeetings;

  const todayStr = useMemo(() => new Date().toDateString(), []);

  const todaysMeetings = useMemo(
    () =>
      combinedMeetings.filter(
        (m) =>
          new Date(m.scheduledDate).toDateString() === new Date().toDateString()
      ),
    [combinedMeetings]
  );

  function handleJoin(meeting: Meeting) {
    showNotification({ message: "Joining meeting...", color: "blue" });
    if (onJoin) return onJoin(meeting);
    if (meeting.meetingLink) window.open(meeting.meetingLink, "_blank");
  }

  function handleView(meetingId: string) {
    setOpenedMeetingModal(true);
  }
  function joinMeeting(meeting: Meeting) {
    showNotification({ message: "Joining meeting...", color: "blue" });
    if (meeting.meetingLink) window.open(meeting.meetingLink, "_blank");
  }
  function editMeeting(meetingId: string) {
    showNotification({ message: `Open edit for ${meetingId}`, color: "blue" });
  }
  function cancelMeeting(meetingId: string) {
    const confirmed = window.confirm(
      "Are you sure you want to cancel this meeting?"
    );
    if (!confirmed) return;

    showNotification({
      message: "Meeting cancelled successfully",
      color: "green",
    });
  }
  return (
    <Box>
      <Stack spacing="md">
        <Title order={3}>Today's Meetings - {todayStr}</Title>

        <Stack>
          {todaysMeetings.length === 0 && (
            <Card withBorder p="lg">
              <Text color="dimmed">
                You have no meetings scheduled for today.
              </Text>
            </Card>
          )}

          {todaysMeetings.map((m) => (
            <Card key={m.id} shadow="sm" p="lg" radius="md" withBorder>
              <Grid>
                <Grid.Col span={8}>
                  <Text weight={700}>{m.title}</Text>
                  <Text color="blue" weight={700} size="sm">
                    <IconClock
                      size={14}
                      style={{ verticalAlign: "middle", marginRight: 6 }}
                    />
                    {new Date(m.scheduledDate).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    -{" "}
                    {new Date(
                      new Date(m.scheduledDate).getTime() + m.duration * 60000
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Grid.Col>

                <Grid.Col
                  span={4}
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Badge
                    color={
                      m.status === "scheduled"
                        ? "teal"
                        : m.status === "ongoing"
                        ? "yellow"
                        : m.status === "completed"
                        ? "blue"
                        : "red"
                    }
                  >
                    {m.status.toUpperCase()}
                  </Badge>
                </Grid.Col>
              </Grid>

              <Group mt="md">
                <Group spacing="md">
                  <Group spacing={6}>
                    <IconUsers />
                    <Text size="sm">{m.attendees?.length ?? 0} Attendees</Text>
                  </Group>

                  <Group spacing={6}>
                    <IconVideo />
                    <Text size="sm">{m.meetingPlatform || "N/A"}</Text>
                  </Group>

                  <Group spacing={6}>
                    <IconFolder />
                    <Text size="sm">{m.projectId || "Project"}</Text>
                  </Group>
                </Group>
              </Group>

              <Text color="dimmed" mt="sm">
                {m.description}
              </Text>

              <Group mt="md">
                <Button
                  size="sm"
                  onClick={() => handleJoin(m)}
                  leftIcon={<IconPlayerPlay />}
                >
                  Join Meeting
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCurrentMeetingId(m.id);
                    // viewMeeting(m.id);
                    handleView(m.id);
                  }}
                  leftIcon={<IconEye />}
                >
                  View Details
                </Button>
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>
      <MeetingDetailsModal
        opened={openedMeetingModal}
        onClose={() => setOpenedMeetingModal(false)}
        currentMeetingId={currentMeetingId}
        onJoin={joinMeeting}
        onEdit={editMeeting}
        onCancel={cancelMeeting}
      />
    </Box>
  );
}
